import React, { useState, useRef, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Typewriter } from 'react-simple-typewriter';
import { Linkedin, Mail, Clock, ArrowUpRight, Check } from "lucide-react";

// Import Komponen Photography Subsite — tidak diubah
import PhotographyHome from "./components/photography/Home";
import About from "./components/photography/About"; 
import Contact from "./components/photography/Contact";
import PhotographyLayout from "./components/photography/PhotographyLayout";

// Import komponen profesional baru
import MainNavbar from "./components/MainNavbar";
import ProfileSummary from "./components/ProfileSummary";
import Experience from "./components/Experience";
import Awards from "./components/Awards";
import SelectedWork from "./components/SelectedWork";
import AssessmentSim from "./components/AssessmentSim";
// import Notes from "./components/Notes"; // temporarily hidden while draft
import NoteDetail from "./components/NoteDetail";

// Wrapper agar Layout (Header Liquid Glass & Footer) teraplikasi di halaman About & Contact
const PhotographyAbout = () => <PhotographyLayout><About /></PhotographyLayout>;
const PhotographyContact = () => <PhotographyLayout><Contact /></PhotographyLayout>;

const MainHome = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [jakartaTime, setJakartaTime] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const formatted = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Jakarta',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(now);
      setJakartaTime(formatted);
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      const triggerHeight = window.innerHeight * 0.8; 
      setIsScrolled(scrollTop > triggerHeight);
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('ivansridana@gmail.com');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch {
      // Fallback: open mailto
      window.location.href = 'mailto:ivansridana@gmail.com';
    }
  };

  return (
    // PRESERVATION LOCK: scroll container — do not change className, ref, or onScroll
    <div 
      ref={scrollRef}
      onScroll={handleScroll}
      className="h-[100svh] md:h-screen w-full max-w-full overflow-x-hidden overflow-y-scroll snap-none md:snap-y md:snap-mandatory bg-stone-50 text-stone-900 scroll-smooth"
    >
      {/* Navbar — appears after hero scroll, built as standalone component */}
      <AnimatePresence>
        {isScrolled && (
          <MainNavbar scrollContainerRef={scrollRef} />
        )}
      </AnimatePresence>

      {/* --- HERO SECTION --- PRESERVATION LOCK: structure, composition, classes, content unchanged */}
      <section className="snap-start h-[100svh] md:h-screen w-full relative flex flex-col justify-end">
        <div className="absolute inset-0 z-0">
          <img 
            src="/hero.jpg" 
            alt="Ketut Ivan Sridana" 
            className="w-full h-full object-cover grayscale brightness-[0.65]" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-transparent to-transparent opacity-80" />
        </div>

        <div className="relative z-10 px-4 sm:px-5 py-6 pb-24 sm:pb-28 md:p-16 w-full max-w-7xl mx-auto flex flex-col justify-end h-full text-stone-50">
          <motion.p 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="font-mono text-[11px] sm:text-xs md:text-sm tracking-[0.2em] mb-3 sm:mb-4 uppercase text-stone-400"
          >
            Digital Integrity // Visual Narrative
          </motion.p>
          
          <motion.h1 
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }}
            className="text-[42px] min-[375px]:text-5xl md:text-8xl font-serif leading-[0.96] mb-6 sm:mb-8 tracking-tighter"
          >
            Ketut <span className="italic font-light">Ivan</span><br/> Sridana
          </motion.h1>

          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            className="font-mono text-[10px] min-[375px]:text-[11px] leading-6 whitespace-nowrap sm:text-xs md:text-base text-yellow-500 h-6"
          >
            <Typewriter 
              words={['Cybersecurity Consultant at EY Indonesia', 'Photographer after hours']} 
              loop={0} cursor cursorStyle='_' typeSpeed={70} deleteSpeed={50} delaySpeed={2000} 
            />
          </motion.div>
        </div>

        {/* Explore Indicator */}
        <motion.div 
           initial={{ opacity: 0, x: '-50%', y: -20 }} animate={{ opacity: 1, x: '-50%', y: 0 }} transition={{ delay: 2, duration: 1 }}
           className="absolute bottom-4 pb-[env(safe-area-inset-bottom)] md:bottom-10 md:pb-0 left-1/2 z-20 flex flex-col items-center gap-2 md:gap-3"
        >
           <span className="text-[10px] text-stone-400 font-mono uppercase tracking-[0.5em] ml-[0.5em]">Explore</span>
           <div className="w-[1px] h-12 bg-stone-700 relative overflow-hidden">
              <motion.div 
                animate={{ y: [0, 48, 48], opacity: [0, 1, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-0 left-0 w-full h-1/2 bg-yellow-500"
              />
           </div>
        </motion.div>
      </section>
      {/* END HERO PRESERVATION LOCK */}

      {/* --- PROFILE SUMMARY (replaces old detail section) --- */}
      <ProfileSummary />

      {/* --- SELECTED WORK --- */}
      <SelectedWork />

      {/* --- ASSESSMENT SIMULATION --- */}
      <AssessmentSim />

      {/* --- EXPERIENCE & EDUCATION --- */}
      <Experience />

      {/* --- HONORS & AWARDS --- */}
      <Awards />

      {/* --- NOTES (temporarily hidden while draft) --- */}
      {/* <Notes /> */}

      {/* --- FOOTER: Architectural Closing Terminal --- */}
      <section className="snap-start bg-stone-900 text-stone-50 w-full px-4 sm:px-5 py-10 sm:py-12 md:px-20 md:py-20 border-t border-stone-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-end gap-8 sm:gap-10">
          
          {/* Left: Signature, Time & Availability */}
          <div className="space-y-4">
            <div>
              <p className="font-serif text-2xl sm:text-3xl italic tracking-tighter leading-none mb-2">
                Ketut <span className="font-light">Ivan</span> Sridana
              </p>
              <p className="font-mono text-[9px] text-stone-400 uppercase tracking-[0.2em]">
                Cybersecurity GRC · EY Indonesia
              </p>
            </div>

            {/* Live Jakarta Clock & Availability */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <div className="inline-flex items-center gap-2 bg-stone-800/80 border border-stone-700/80 px-2.5 py-1 rounded-sm text-[11px] font-mono text-stone-300">
                <Clock size={12} className="text-yellow-500" aria-hidden="true" />
                <span>{jakartaTime || '12:00:00'} WIB</span>
                <span className="text-stone-500 text-[9px]">UTC+7 · Jakarta</span>
              </div>

              <div className="inline-flex items-center gap-1.5 bg-stone-800/80 border border-stone-700/80 px-2.5 py-1 rounded-sm text-[10.5px] font-mono text-stone-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
                <span>Open for Advisory &amp; Technical Dialogue</span>
              </div>
            </div>
          </div>

          {/* Right: Contact Actions & Copyright */}
          <div className="flex flex-col items-start md:items-end gap-5">
            <div className="flex flex-wrap items-center gap-3">
              {/* Copy Email Button */}
              <button
                onClick={handleCopyEmail}
                className="group flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-stone-300 hover:text-white bg-stone-800 hover:bg-stone-700 border border-stone-700 px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
                aria-label="Copy email address to clipboard"
              >
                {emailCopied ? (
                  <>
                    <Check size={13} className="text-yellow-400" aria-hidden="true" />
                    <span className="text-yellow-400">Copied to Clipboard</span>
                  </>
                ) : (
                  <>
                    <Mail size={13} className="text-stone-400 group-hover:text-white transition-colors" aria-hidden="true" />
                    <span>ivansridana@gmail.com</span>
                  </>
                )}
              </button>

              {/* LinkedIn Link */}
              <a
                href="https://www.linkedin.com/in/ivansridana/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-stone-300 hover:text-white bg-stone-800 hover:bg-stone-700 border border-stone-700 px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
              >
                <Linkedin size={13} aria-hidden="true" />
                <span>LinkedIn</span>
                <ArrowUpRight size={12} className="text-stone-500" aria-hidden="true" />
              </a>
            </div>

            <div className="flex items-center gap-4 text-stone-500 font-mono text-[9px] uppercase tracking-widest">
              <span>Jakarta, Indonesia</span>
              <span>·</span>
              <span>&copy; 2026</span>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        {/* Halaman Utama */}
        <Route path="/" element={<MainHome />} />
        <Route path="/notes/:slug" element={<NoteDetail />} />

        {/* Photography Subsite — tidak diubah */}
        <Route path="/photography" element={<PhotographyHome />} />
        <Route path="/photography/about" element={<PhotographyAbout />} />
        <Route path="/photography/contact" element={<PhotographyContact />} />
      </Routes>
    </Router>
  );
}

export default App;
